import { stripShopifyGid, toShopifyProductGid } from "@codway-dev/shared";
import type { TriggerProduct } from "../store/triggers.mapping";

export async function openTriggerProductsPicker(
  currentProducts: TriggerProduct[],
  onSelect: (products: TriggerProduct[]) => void,
  onError?: (err: unknown) => void
): Promise<void> {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: true,
      // Reconstitute the GID — `resourcePicker` expects the full GID
      // format for `selectionIds` to round-trip pre-selection.
      selectionIds: currentProducts.map((p) => ({
        id: toShopifyProductGid(p.id),
      })),
    });
    if (!selected) return;
    onSelect(
      selected.map((p) => ({
        id: stripShopifyGid(p.id),
        title: p.title,
        imageUrl: p.images[0]?.originalSrc,
      }))
    );
  } catch (err) {
    onError?.(err);
  }
}

// ─── Bundle picker ────────────────────────────────────────────────────────────

export interface BundleVariantPick {
  productId: string;
  variantId: string;
  productTitle: string;
  variantTitle?: string;
  imageUrl?: string;
  basePrice: number;
}

// Opens the multi-product resource picker and flattens the result so each
// selected variant becomes one pick. The Shopify picker already supports
// product-level and variant-level selection in the same UI — merchants can
// pick a whole product (all its variants come back) or drill in and pick
// specific variants. We just consume what's returned.
//
// Caller decides whether each pick becomes a fixed item or a configurable
// option — this util stays bundle-mode-agnostic.
export async function openBundleResourcePicker(
  onSelect: (picks: BundleVariantPick[]) => void,
  onError?: (err: unknown) => void
): Promise<void> {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: true,
    });
    if (!selected) return;

    const picks: BundleVariantPick[] = [];
    for (const product of selected) {
      const productId = stripShopifyGid(product.id);
      const productImage = product.images[0]?.originalSrc;
      for (const variant of product.variants) {
        const isDefaultTitle =
          !variant.title || variant.title === "Default Title";
        picks.push({
          productId,
          variantId: stripShopifyGid(variant.id),
          productTitle: product.title,
          ...(isDefaultTitle ? {} : { variantTitle: variant.title }),
          imageUrl:
            variant.image?.originalSrc ?? variant.image?.src ?? productImage,
          basePrice: parseFloat(variant.price ?? "0") || 0,
        });
      }
    }

    onSelect(picks);
  } catch (err) {
    onError?.(err);
  }
}
